import type { QuestionDefinition } from "@reno-estimator/core";

/**
 * Attic's full question set (Phase 13B) - see rules/attic.ts for the
 * matching calculator. Per the brief, insulation and finishing are
 * independent: `attic_flooring` only shows when
 * `attic_finishing_scope` is "finishing_to_living_space", via
 * `displayIf` - the same gating mechanism deck/patio's kind-specific
 * material questions use - but insulation, electrical, and access are
 * asked (and priced) regardless of whether the attic is being finished
 * at all.
 */
export const atticQuestions: QuestionDefinition[] = [
  {
    id: "attic_insulation",
    projectTypes: ["attic"],
    inputType: "single_select",
    prompt: "What insulation upgrade, if any?",
    helpText: "Usually the single biggest cost/value driver for an attic project - priced whether or not you're finishing the attic into living space.",
    required: false,
    options: [
      { id: "not_changing", label: "Not changing" },
      { id: "standard_upgrade", label: "Standard upgrade", description: "Topping up blown-in insulation to a standard R-value." },
      { id: "high_r_upgrade", label: "High R-value upgrade", description: "A deeper blown-in upgrade for maximum standard insulation performance." },
      { id: "spray_foam_premium", label: "Spray foam (premium)", description: "Closed-cell spray foam - highest performance, highest cost." }
    ],
    order: 180
  },
  {
    id: "attic_finishing_scope",
    projectTypes: ["attic"],
    inputType: "single_select",
    prompt: "Is the attic being finished into living space, or is this insulation/storage only?",
    required: true,
    options: [
      { id: "not_finishing", label: "Not finishing", description: "Insulation and/or storage only - no living space conversion." },
      { id: "finishing_to_living_space", label: "Finishing into living space" }
    ],
    order: 190
  },
  {
    id: "attic_flooring",
    projectTypes: ["attic"],
    inputType: "single_select",
    prompt: "What flooring for the finished attic space?",
    required: false,
    displayIf: [{ questionId: "attic_finishing_scope", equalsAnyOf: ["finishing_to_living_space"] }],
    options: [
      { id: "not_changing", label: "Not changing" },
      { id: "basic", label: "Basic" },
      { id: "mid_range", label: "Mid-range" },
      { id: "premium", label: "Premium" }
    ],
    order: 200
  },
  {
    id: "attic_electrical",
    projectTypes: ["attic"],
    inputType: "single_select",
    prompt: "What electrical work, if any?",
    required: false,
    options: [
      { id: "none", label: "None" },
      { id: "basic_lighting", label: "Basic lighting", description: "A light fixture or two and a switch." },
      { id: "full_wiring", label: "Full wiring", description: "Outlets, multiple fixtures, and/or a dedicated circuit for a finished space." }
    ],
    order: 210
  },
  {
    id: "attic_access",
    projectTypes: ["attic"],
    inputType: "single_select",
    prompt: "What kind of attic access is planned?",
    helpText: "A real cost driver, not a cosmetic detail - a scuttle hole is not adequate access/egress for finished living space.",
    required: false,
    options: [
      { id: "no_change", label: "No change to existing access" },
      { id: "pull_down_stairs", label: "Pull-down stairs" },
      { id: "permanent_staircase", label: "Permanent staircase", description: "A built, code-compliant staircase - typically required for finished living space." },
      { id: "scuttle_hole", label: "Scuttle hole only", description: "A simple hatch opening - insulation/storage access only." }
    ],
    order: 220
  },
  {
    id: "attic_structural_limitations",
    projectTypes: ["attic"],
    inputType: "single_select",
    prompt: "What's the roof framing like, and is headroom sufficient?",
    helpText: "Roof trusses (vs. rafters) often can't be finished without an engineer, and most building codes have a minimum ceiling height for finished space - this genuinely can't be assessed from a questionnaire alone, so answer as best you can.",
    required: false,
    options: [
      { id: "standard_rafters", label: "Standard rafter framing, adequate headroom" },
      { id: "attic_trusses", label: "Roof trusses", description: "Pre-fabricated triangulated framing - commonly not designed to support finished space without engineering." },
      { id: "insufficient_headroom", label: "Headroom looks tight", description: "Ceiling height may not meet the code minimum for finished space over enough of the floor area." },
      { id: "not_sure", label: "Not sure" }
    ],
    order: 230
  }
];
